import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Icon from '@material-ui/core/Icon';
import classnames from 'classnames';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { onChangeMapFilters } from '../../../ac/onChangeMapFilters';
import { StandardRadioGroup } from '../StandardRadioGroup';
import { withDisplayedLayersCheck } from '../../../HOC/withDisplayedLayersCheck';
import { LegendRenderer } from '../../LegendRenderer/LegendRenderer';
import { AGG_TRACKS_COHORT_LEGEND, AGG_TRACKS_COHORT_SHARE_LEGEND } from '../../../constants/legends';
import * as V from '../../../constants/variables';
import styles from '../FilterSidebar.module.css';

const H3_ZOOMS = [ 6, 7, 8, 9, 10, 11, 12, 'auto' ];
const PERIOD_TYPES = V.aggregatedTracksDayFilterValues;
// DA-15259: 13 когорт — плоский список нечитаем, поэтому радиокнопки разбиты
// подзаголовками групп (Поминутка / Пакеты минут / Безлимит / Whoosh Pass).
// Группа берётся из V.aggTracksCohortValues, порядок сохраняется как в константе.
const AggTracksCohortFilterComponent = ({
    onChangeMapFilters,
    aggTracksCohortH3Zoom,
    aggTracksCohortType,
    aggTracksCohortPeriod,
    aggTracksCohortMode,
}) => {
    const [ isH3ZoomExpanded, setIsH3ZoomExpanded ] = useState(false);
    const handleCohortChange = ({ target: { value } }) => {
        onChangeMapFilters('aggTracksCohortType', value);
    };
    // Клик по верхнему уровню: у группы выбираем её первую подгруппу, у листа — сам лист.
    const handleGroupChange = ({ target: { value } }) => {
        const item = V.aggTracksCohortValues.find(({ value: itemValue }) => itemValue === value);

        onChangeMapFilters('aggTracksCohortType', item && item.children ? item.children[0].value : value);
    };
    const handlePeriodChange = ({ target: { value } }) => {
        onChangeMapFilters('aggTracksCohortPeriod', value);
    };
    const handleModeChange = ({ target: { value } }) => {
        onChangeMapFilters('aggTracksCohortMode', value);
    };
    const handleH3ZoomChange = ({ target: { value } }) => {
        onChangeMapFilters('aggTracksCohortH3Zoom', value);
    };
    const toggleH3Zoom = () => {
        setIsH3ZoomExpanded(!isH3ZoomExpanded);
    };
    // Выбранный лист и его группа: верхний уровень подсвечивается по группе выбранного листа.
    const selected = V.findAggTracksCohort(aggTracksCohortType);
    const selectedGroup = selected && selected.parent ? selected.parent.value : null;
    const renderRadio = ({ value, label }, isChecked, onChange) => (
        <FormControlLabel
            key={value}
            className={styles.isochroneFilterLabel}
            control={
                <Radio
                    checked={isChecked}
                    onChange={onChange}
                    value={value}
                    className={classnames(
                        styles.isochroneFilterInput, {
                            [styles.checkedIsochroneFilterInput]: isChecked,
                        }
                    )}
                    size="small"
                />
            }
            label={i18n._(label)}
            labelPlacement="end"
        />
    );
    // Для «Всех поездок» доля тождественно равна 100% — тумблер режима прячем,
    // и легенду показываем абсолютную независимо от сохранённого режима.
    const isShareAvailable = aggTracksCohortType !== 'all';
    const isShare = isShareAvailable && aggTracksCohortMode === 'share';

    return (
        <div>
            <div className={styles.legHead}>{t`Модель тарификации`}:</div>
            <StandardRadioGroup
                value={selectedGroup || aggTracksCohortType}
                onChange={handleGroupChange}
            >
                {
                    V.aggTracksCohortValues.map(item => (item.children
                        ? (
                            // Группа: клик по ней выбирает первую подгруппу, подгруппы
                            // раскрываются только у выбранной группы.
                            <React.Fragment key={item.value}>
                                {renderRadio(item, selectedGroup === item.value, handleGroupChange)}
                                {selectedGroup === item.value && (
                                    <div className={styles.nestedRadioGroup}>
                                        {item.children.map(child => renderRadio(
                                            child,
                                            aggTracksCohortType === child.value,
                                            handleCohortChange
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        )
                        // Лист верхнего уровня («Все поездки», «Прочее») — без подгрупп.
                        : renderRadio(item, aggTracksCohortType === item.value, handleCohortChange)))
                }
            </StandardRadioGroup>

            <div className={styles.legHead}>{t`Период`}:</div>
            <StandardRadioGroup
                value={aggTracksCohortPeriod}
                onChange={handlePeriodChange}
            >
                {
                    PERIOD_TYPES.map(({ value, label }) => (
                        <FormControlLabel
                            key={value}
                            className={styles.isochroneFilterLabel}
                            control={
                                <Radio
                                    checked={aggTracksCohortPeriod === value}
                                    onChange={handlePeriodChange}
                                    value={value}
                                    className={classnames(
                                        styles.isochroneFilterInput, {
                                            [styles.checkedIsochroneFilterInput]: aggTracksCohortPeriod === value,
                                        }
                                    )}
                                    size="small"
                                />
                            }
                            label={i18n._(label)}
                            labelPlacement="end"
                        />
                    ))
                }
            </StandardRadioGroup>

            {isShareAvailable && (
                <>
                    <div className={styles.legHead}>{t`Метрика`}:</div>
                    <StandardRadioGroup
                        value={aggTracksCohortMode}
                        onChange={handleModeChange}
                    >
                        {
                            V.aggTracksCohortModeValues.map(({ value, label }) => (
                                <FormControlLabel
                                    key={value}
                                    className={styles.isochroneFilterLabel}
                                    control={
                                        <Radio
                                            checked={aggTracksCohortMode === value}
                                            onChange={handleModeChange}
                                            value={value}
                                            className={classnames(
                                                styles.isochroneFilterInput, {
                                                    [styles.checkedIsochroneFilterInput]: aggTracksCohortMode === value,
                                                }
                                            )}
                                            size="small"
                                        />
                                    }
                                    label={i18n._(label)}
                                    labelPlacement="end"
                                />
                            ))
                        }
                    </StandardRadioGroup>
                </>
            )}

            <div
                className={classnames(styles.legHead, {
                    [styles.expandableLegHead]: true,
                    [styles.expandableLegHeadOpen]: isH3ZoomExpanded,
                })}
                onClick={toggleH3Zoom}
                style={{ cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center' }}
            >
                {t`H3 Zoom`}:
                <Icon style={{ marginLeft: 'auto', transition: 'transform 0.25s ease' }}>expand_more</Icon>
            </div>
            {isH3ZoomExpanded && (
                <StandardRadioGroup
                    value={String(aggTracksCohortH3Zoom)}
                    onChange={handleH3ZoomChange}
                >
                    {
                        H3_ZOOMS.map(zoom => (
                            <FormControlLabel
                                key={zoom}
                                className={styles.isochroneFilterLabel}
                                control={
                                    <Radio
                                        checked={String(aggTracksCohortH3Zoom) === String(zoom)}
                                        onChange={handleH3ZoomChange}
                                        value={String(zoom)}
                                        className={classnames(
                                            styles.isochroneFilterInput, {
                                                [styles.checkedIsochroneFilterInput]: String(aggTracksCohortH3Zoom) === String(zoom),
                                            }
                                        )}
                                        size="small"
                                    />
                                }
                                label={zoom}
                            />
                        ))
                    }
                </StandardRadioGroup>
            )}

            <LegendRenderer config={isShare ? AGG_TRACKS_COHORT_SHARE_LEGEND : AGG_TRACKS_COHORT_LEGEND} />
        </div>
    );
};
const mapStateToProps = ({ mapFilters: {
    aggTracksCohortH3Zoom,
    aggTracksCohortType,
    aggTracksCohortPeriod,
    aggTracksCohortMode,
} }) => ({
    aggTracksCohortH3Zoom,
    aggTracksCohortType,
    aggTracksCohortPeriod,
    aggTracksCohortMode,
});
const ConnectedComponent = connect(mapStateToProps, { onChangeMapFilters })(AggTracksCohortFilterComponent);

export const AggTracksCohortFilter = withDisplayedLayersCheck(
    ConnectedComponent,
    t`Треки по моделям тарификации (7 дней)`
);
